import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { Shield, TrendingUp, AlertTriangle, IndianRupee, Wallet, Heart, Car, Zap, ShoppingBag, Home, Lightbulb } from 'lucide-react';

const InteractiveEmergencyFund = () => {
  const [inputs, setInputs] = useState({
    monthlyExpenses: 50000,
    monthsCoverage: 6,
    currentEmergencyFund: 0,
    expectedReturn: 6
  });
  const [result, setResult] = useState(null);
  const [breakdown, setBreakdown] = useState(null);

  const calculateEmergencyFund = useCallback(() => {
    const monthly = parseFloat(inputs.monthlyExpenses);
    const months = parseInt(inputs.monthsCoverage);
    const current = parseFloat(inputs.currentEmergencyFund || 0);

    const totalRequired = monthly * months;
    const gap = Math.max(totalRequired - current, 0);
    
    const targetMonths = 12;
    const monthlySIP = gap / targetMonths;

    setResult({
      totalRequired: Math.round(totalRequired),
      currentFund: current,
      gap: Math.round(gap),
      monthlySIP: Math.round(monthlySIP),
      targetMonths: targetMonths,
      fundedPercent: Math.min((current / totalRequired) * 100, 100).toFixed(1)
    });

    setBreakdown({
      housing: Math.round(monthly * 0.30),
      food: Math.round(monthly * 0.20),
      utilities: Math.round(monthly * 0.10),
      insurance: Math.round(monthly * 0.10),
      transportation: Math.round(monthly * 0.10),
      healthcare: Math.round(monthly * 0.10),
      miscellaneous: Math.round(monthly * 0.10)
    });
  }, [inputs]);

  useEffect(() => {
    calculateEmergencyFund();
  }, [calculateEmergencyFund]);

  const formatCurrency = (value) => {
    if (value >= 100000) {
      return `\u20b9${(value / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const coverageOptions = [
    { months: 3, label: '3 Months', risk: 'Basic', color: 'yellow' },
    { months: 6, label: '6 Months', risk: 'Recommended', color: 'green' },
    { months: 9, label: '9 Months', risk: 'Conservative', color: 'blue' },
    { months: 12, label: '12 Months', risk: 'Very Safe', color: 'purple' }
  ];

  const sliderStyles = `
    .slider-amber::-webkit-slider-thumb {
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f59e0b, #f97316);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(245, 158, 11, 0.5);
    }
    .slider-amber::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f59e0b, #f97316);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(245, 158, 11, 0.5);
      border: none;
    }
  `;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Emergency Fund Calculator
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Build your financial safety net. Be prepared for life&apos;s unexpected moments!
            </p>
          </div>

          <div className="mb-8">
            <p className="text-white/60 text-sm mb-3 text-center">Select Coverage Period:</p>
            <div className="flex flex-wrap justify-center gap-3">
              {coverageOptions.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputs({ ...inputs, monthsCoverage: option.months })}
                  className={`px-5 py-3 border rounded-xl text-sm transition-all ${
                    inputs.monthsCoverage === option.months
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <div className="font-semibold">{option.label}</div>
                  <div className="text-xs opacity-60">{option.risk}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <Wallet className="w-6 h-6 text-amber-400" />
                Your Monthly Expenses
              </h2>
              
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Monthly Expenses</label>
                    <span className="text-amber-400 font-bold text-xl">{formatCurrency(inputs.monthlyExpenses)}</span>
                  </div>
                  <input
                    type="range"
                    min="20000"
                    max="500000"
                    step="5000"
                    value={inputs.monthlyExpenses}
                    onChange={(e) => setInputs({...inputs, monthlyExpenses: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-amber"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>\u20b920,000</span>
                    <span>\u20b95,00,000</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Coverage Period</label>
                    <span className="text-orange-400 font-bold text-xl">{inputs.monthsCoverage} Months</span>
                  </div>
                  <input
                    type="range"
                    min="3"
                    max="12"
                    step="1"
                    value={inputs.monthsCoverage}
                    onChange={(e) => setInputs({...inputs, monthsCoverage: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-amber"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>3 Months</span>
                    <span>12 Months</span>
                  </div>
                </div>

                <div>
                  <label className="text-white font-medium mb-2 block">Current Emergency Fund</label>
                  <input
                    type="number"
                    value={inputs.currentEmergencyFund}
                    onChange={(e) => setInputs({...inputs, currentEmergencyFund: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-amber-500"
                    placeholder="0"
                  />
                </div>

                {breakdown && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-white/70 text-sm mb-3">Typical Expense Breakdown</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Home className="w-3 h-3 text-blue-400" />
                        <span className="text-white/60">Housing:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.housing)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="w-3 h-3 text-green-400" />
                        <span className="text-white/60">Food:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.food)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Zap className="w-3 h-3 text-yellow-400" />
                        <span className="text-white/60">Utilities:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.utilities)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Heart className="w-3 h-3 text-red-400" />
                        <span className="text-white/60">Healthcare:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.healthcare)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Car className="w-3 h-3 text-purple-400" />
                        <span className="text-white/60">Transport:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.transportation)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="w-3 h-3 text-cyan-400" />
                        <span className="text-white/60">Insurance:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.insurance)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {result && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur-xl rounded-3xl p-8 border border-amber-500/30">
                  <p className="text-white/60 text-sm mb-2">\ud83d\udee1\ufe0f Emergency Fund Target</p>
                  <div className="flex items-center gap-3 mb-4">
                    <IndianRupee className="w-6 h-6 text-amber-400" />
                    <h3 className="text-white/70 text-sm">Total Required</h3>
                  </div>
                  <p className="text-5xl font-bold text-white mb-2">{formatCurrency(result.totalRequired)}</p>
                  <p className="text-white/50 text-sm">for {inputs.monthsCoverage} months coverage</p>
                </div>

                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-white font-semibold">Your Progress</p>
                    <span className={`text-lg font-bold ${parseFloat(result.fundedPercent) >= 100 ? 'text-green-400' : 'text-amber-400'}`}>
                      {result.fundedPercent}%
                    </span>
                  </div>
                  <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${parseFloat(result.fundedPercent) >= 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}
                      style={{width: `${Math.min(parseFloat(result.fundedPercent), 100)}%`}}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-white/60">Current: {formatCurrency(result.currentFund)}</span>
                    <span className="text-white/60">Target: {formatCurrency(result.totalRequired)}</span>
                  </div>
                </div>

                {result.gap > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 backdrop-blur-xl rounded-2xl p-6 border border-red-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        <p className="text-white/50 text-xs">Funding Gap</p>
                      </div>
                      <p className="text-2xl font-bold text-red-400">{formatCurrency(result.gap)}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 backdrop-blur-xl rounded-2xl p-6 border border-green-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <p className="text-white/50 text-xs">Monthly SIP (12 mo)</p>
                      </div>
                      <p className="text-2xl font-bold text-green-400">{formatCurrency(result.monthlySIP)}</p>
                    </div>
                  </div>
                )}

                {result.gap === 0 && (
                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-xl rounded-2xl p-6 border border-green-500/30 text-center">
                    <div className="text-4xl mb-2">\ud83c\udf89</div>
                    <p className="text-green-400 font-bold text-xl">Congratulations!</p>
                    <p className="text-white/70 mt-2">Your emergency fund is fully funded. You&apos;re financially secure!</p>
                  </div>
                )}

                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <p className="text-white font-semibold mb-4">\ud83d\udcb0 Where to Park Your Emergency Fund</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white font-medium">Liquid Funds</p>
                        <p className="text-white/50 text-xs">Instant redemption, 4-6% returns</p>
                      </div>
                      <span className="text-green-400 text-sm font-medium">Recommended</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white font-medium">Savings Account</p>
                        <p className="text-white/50 text-xs">High liquidity, 3-4% returns</p>
                      </div>
                      <span className="text-blue-400 text-sm font-medium">Good Option</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white font-medium">Short-term FDs</p>
                        <p className="text-white/50 text-xs">Laddered approach, 5-6% returns</p>
                      </div>
                      <span className="text-cyan-400 text-sm font-medium">Alternative</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-2xl p-6 border border-amber-500/30">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-semibold mb-2">Emergency Fund Tips</p>
                      <p className="text-white/80 text-sm">
                        {inputs.monthsCoverage < 6 
                          ? "Consider building up to 6 months coverage for better security. Job losses typically take 3-6 months to recover from."
                          : inputs.monthsCoverage >= 9
                          ? "Great coverage! Consider splitting between liquid funds (3-4 months) and overnight funds (remaining) for optimal returns."
                          : "6 months is the sweet spot for most professionals. Keep it in liquid funds for instant access when needed."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-all duration-300"
            >
              Get Expert Emergency Planning Advice
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InteractiveEmergencyFund;
